import type { CSSProperties, ReactNode } from "react";
import type { PerpLeg, PredictionLeg } from "../../lib/composePayoff";
import type { SelectedPerp, SelectedPred } from "../../types";

const HL_MINT = "#97FCE4";

function hueFor(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function predTint(seed: string): { tint: string; gradient: string } {
  const hue = hueFor(seed);
  const tint = `hsla(${hue}, 72%, 58%, 0.22)`;
  const gradient = `
    radial-gradient(120% 120% at 0% 0%, hsla(${hue}, 72%, 58%, 0.18), transparent 55%),
    linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)
  `;
  return { tint, gradient };
}

function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n >= 100 ? 0 : 2,
  });
}

const mono10: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  fontWeight: 600,
};

// ----- Empty state shared by both leg types ----------------------------------

function EmptyLegCard({
  color,
  venueShort,
  venueLong,
  onClick,
}: {
  color: string;
  venueShort: string;
  venueLong: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        padding: "14px 12px",
        borderRadius: 4,
        border: `1px dashed ${color}66`,
        background: `${color}08`,
        color,
        fontFamily: "var(--f-mono)",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        cursor: "pointer",
        boxShadow: `inset 2px 0 0 ${color}`,
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          style={{
            fontSize: 12,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          + Pick a {venueShort}
        </span>
        <span
          style={{
            fontSize: 9,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontWeight: 500,
            color: `${color}aa`,
          }}
        >
          {venueLong}
        </span>
      </span>
      <span
        style={{
          fontSize: 9,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: `${color}cc`,
          flexShrink: 0,
        }}
      >
        BROWSE →
      </span>
    </button>
  );
}

// ----- Container with venue stripe -------------------------------------------

function CardShell({
  color,
  background,
  border,
  onClick,
  children,
  ariaLabel,
}: {
  color: string;
  background?: string;
  border?: string;
  onClick?: () => void;
  children: ReactNode;
  ariaLabel?: string;
}) {
  const interactive = !!onClick;
  return (
    <div
      role={interactive ? "button" : undefined}
      aria-label={ariaLabel}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!interactive) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      style={{
        position: "relative",
        padding: "11px 12px 12px",
        borderRadius: 4,
        background:
          background ??
          `linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)`,
        border: border ?? `1px solid ${color}33`,
        boxShadow: `inset 2px 0 0 ${color}, 0 1px 0 rgba(0,0,0,0.3)`,
        cursor: interactive ? "pointer" : "default",
        WebkitTapHighlightColor: "transparent",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

// ----- Perp leg card ---------------------------------------------------------

export function MobilePerpCard({
  market,
  leg,
  color,
  onTap,
  onSelectMarket,
}: {
  market: SelectedPerp | null;
  leg: PerpLeg | null;
  color: string;
  onTap: () => void;
  onSelectMarket: () => void;
}) {
  if (!market) {
    return (
      <EmptyLegCard
        color={color}
        venueShort="Perp"
        venueLong="Hyperliquid"
        onClick={onSelectMarket}
      />
    );
  }
  const chgColor = market.dayChangePct >= 0 ? "var(--up)" : "var(--dn)";
  return (
    <CardShell
      color={color}
      background={`
        linear-gradient(135deg, ${HL_MINT}0F 0%, transparent 55%),
        linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)
      `}
      border={`1px solid ${HL_MINT}22`}
      onClick={onTap}
      ariaLabel={`Edit perp position on ${market.coin}`}
    >
      {/* Top row */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <img
          src="/hyperliquid/icon/icon-mintgreen.svg"
          alt=""
          style={{ width: 14, height: 14, flexShrink: 0 }}
        />
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 13,
            fontWeight: 600,
            color: "var(--fg-max)",
            letterSpacing: "0.04em",
          }}
        >
          {market.coin}
        </span>
        <span
          style={{
            ...mono10,
            fontSize: 8,
            color: HL_MINT,
            border: `1px solid ${HL_MINT}55`,
            background: `${HL_MINT}10`,
            padding: "1px 5px",
            borderRadius: 2,
            letterSpacing: "0.14em",
          }}
        >
          {market.maxLeverage}× MAX
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--f-mono)",
            fontSize: 13,
            color: "var(--fg-max)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatPrice(market.markPx)}
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 11,
            color: chgColor,
            minWidth: 52,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {market.dayChangePct >= 0 ? "+" : ""}
          {market.dayChangePct.toFixed(2)}%
        </span>
      </div>

      {/* Position summary or 'configure' prompt */}
      {leg ? (
        <PerpPositionRow leg={leg} coin={market.coin} color={color} />
      ) : (
        <ConfigurePrompt color={color} />
      )}
    </CardShell>
  );
}

function PerpPositionRow({
  leg,
  coin,
  color,
}: {
  leg: PerpLeg;
  coin: string;
  color: string;
}) {
  const sideColor = leg.side === "long" ? "var(--up)" : "var(--dn)";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 8px",
        borderRadius: 2,
        background: "rgba(0,0,0,0.28)",
        border: "1px solid var(--line-1)",
        boxShadow: `inset 2px 0 0 ${sideColor}`,
      }}
    >
      <span
        style={{
          ...mono10,
          fontSize: 9,
          fontWeight: 700,
          color: sideColor,
          border: `1px solid ${sideColor}55`,
          background: `${sideColor}15`,
          padding: "2px 6px",
          borderRadius: 2,
          letterSpacing: "0.18em",
        }}
      >
        {leg.side.toUpperCase()}
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          color: "var(--fg-hi)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {leg.size} {coin}
      </span>
      <span style={{ marginLeft: "auto", ...mono10, fontSize: 9, color }}>
        {leg.leverage}×
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          color: "var(--fg-meta)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        @ {formatPrice(leg.entry)}
      </span>
    </div>
  );
}

// ----- Pred leg card ---------------------------------------------------------

export function MobilePredCard({
  market,
  leg,
  color,
  onTap,
  onSelectMarket,
}: {
  market: SelectedPred | null;
  leg: PredictionLeg | null;
  color: string;
  onTap: () => void;
  onSelectMarket: () => void;
}) {
  if (!market) {
    return (
      <EmptyLegCard
        color={color}
        venueShort="Prediction"
        venueLong="Polymarket"
        onClick={onSelectMarket}
      />
    );
  }
  const seed = market.category ?? market.id ?? market.question;
  const { tint, gradient } = predTint(seed);
  return (
    <CardShell
      color={color}
      background={gradient}
      border={`1px solid ${tint}`}
      onClick={onTap}
      ariaLabel="Edit prediction position"
    >
      <img
        src="/polymarket/logos/icon-blue.svg"
        alt=""
        aria-hidden
        style={{
          position: "absolute",
          right: 10,
          bottom: 10,
          height: 13,
          opacity: 0.4,
          pointerEvents: "none",
        }}
      />
      {/* Top row — image + question */}
      <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
        {market.image ? (
          <img
            src={market.image}
            alt=""
            style={{
              width: 26,
              height: 26,
              flexShrink: 0,
              objectFit: "cover",
              border: `1px solid ${tint}`,
              borderRadius: 2,
              boxShadow: `0 0 0 2px rgba(0,0,0,0.2)`,
            }}
          />
        ) : (
          <img
            src="/polymarket/logos/icon-blue.svg"
            alt=""
            style={{ width: 26, height: 26, flexShrink: 0, opacity: 0.9 }}
          />
        )}
        <div
          style={{
            fontFamily: "var(--f-sans)",
            fontSize: 12,
            color: "var(--fg-max)",
            lineHeight: 1.3,
            minWidth: 0,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            flex: 1,
          }}
          title={market.question}
        >
          {market.question}
        </div>
      </div>
      {leg ? (
        <PredPositionRow leg={leg} />
      ) : (
        <ConfigurePrompt color={color} />
      )}
    </CardShell>
  );
}

function PredPositionRow({ leg }: { leg: PredictionLeg }) {
  const outcomeColor = leg.outcome === "yes" ? "var(--up)" : "var(--dn)";
  const directionOp = leg.direction === "above" ? "≥" : "≤";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 8px",
        borderRadius: 2,
        background: "rgba(0,0,0,0.28)",
        border: "1px solid var(--line-1)",
        boxShadow: `inset 2px 0 0 ${outcomeColor}`,
      }}
    >
      <span
        style={{
          ...mono10,
          fontSize: 9,
          fontWeight: 700,
          color: outcomeColor,
          border: `1px solid ${outcomeColor}55`,
          background: `${outcomeColor}15`,
          padding: "2px 6px",
          borderRadius: 2,
          letterSpacing: "0.18em",
        }}
      >
        {leg.outcome.toUpperCase()}
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          color: "var(--fg-hi)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {leg.shares} @ {leg.entryCents.toFixed(1)}¢
      </span>
      <span
        style={{
          marginLeft: "auto",
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          color: "var(--fg-meta)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {directionOp} {formatPrice(leg.threshold)}
      </span>
    </div>
  );
}

// ----- Shared 'configure' prompt --------------------------------------------

function ConfigurePrompt({ color }: { color: string }) {
  return (
    <div
      style={{
        ...mono10,
        fontSize: 10,
        color: `${color}cc`,
        padding: "5px 8px",
        borderRadius: 2,
        background: "rgba(0,0,0,0.2)",
        border: `1px dashed ${color}55`,
      }}
    >
      Tap to configure position →
    </div>
  );
}
